/**
 * Logs route — Live log streaming via SSE.
 */

import { useState, useEffect, useRef, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { Terminal, Play, Pause, Trash2, Filter, Wifi, WifiOff } from "lucide-react";
import { CorpoCard } from "../components/ui/CorpoCard";
import { CRTHeader } from "../components/ui/CRTHeader";
import { GlyphButton } from "../components/ui/GlyphButton";
import { HexBadge } from "../components/ui/HexBadge";
import { useSSE } from "../hooks/useSSE";
import { useLogsStream, useEventStreamStatus } from "../hooks/useEventStream";

interface LogEntry {
  timestamp: string;
  level: "info" | "warn" | "error" | "debug";
  message: string;
  service: string;
}

interface ServicesResponse {
  services: string[];
}

function getLevelBadgeVariant(level: LogEntry["level"]): "default" | "warning" | "danger" | "accent" {
  switch (level) {
    case "error":
      return "danger";
    case "warn":
      return "warning";
    case "debug":
      return "accent";
    default:
      return "default";
  }
}

export function LogsRoute() {
  const [selectedService, setSelectedService] = useState<string>("black-wall-api");
  const [autoScroll, setAutoScroll] = useState(true);
  const [paused, setPaused] = useState(false);
  const [levelFilter, setLevelFilter] = useState<"all" | LogEntry["level"]>("all");
  const [localLogs, setLocalLogs] = useState<LogEntry[]>([]);
  
  const logsEndRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // WebSocket-based log streaming
  const { logs: wsLogs, isConnected: wsConnected, clearLogs } = useLogsStream(500);
  const { state: wsState } = useEventStreamStatus();

  // Fetch available services (for display only now)
  const { data: servicesData } = useQuery({
    queryKey: ["log-services"],
    queryFn: async () => {
      const res = await fetch("/api/logs/services");
      return res.json() as Promise<ServicesResponse>;
    },
  });

  const services = servicesData?.services ?? ["black-wall-api", "openclaw"];

  // SSE streaming (fallback/alternate source)
  const { isStreaming, error, start, stop } = useSSE<LogEntry>({
    onEvent: (_event, data) => {
      if (!paused) {
        setLocalLogs((prev) => {
          const newLogs = [...prev, data];
          if (newLogs.length > 500) {
            return newLogs.slice(-500);
          }
          return newLogs;
        });
      }
    },
    onError: (err) => {
      console.error("Log stream error:", err);
    },
  });

  // Combine WebSocket logs with local logs (WS is primary)
  // Filter by selected service
  const logs = wsLogs
    .filter((log) => log.source === selectedService || selectedService === "all")
    .map((log) => ({
      timestamp: new Date(log.timestamp).toISOString().substring(11, 19),
      level: log.level as LogEntry["level"],
      message: log.message,
      service: log.source,
    }));

  // Auto-scroll effect
  useEffect(() => {
    if (autoScroll && !paused && logsEndRef.current) {
      logsEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [logs, autoScroll, paused]);

  // Start SSE streaming (for services not covered by WS)
  const startStream = useCallback(() => {
    setLocalLogs([]);
    start(`/api/logs/stream?service=${encodeURIComponent(selectedService)}`);
  }, [selectedService, start]);

  const handleServiceChange = (service: string) => {
    stop();
    setSelectedService(service);
    setLocalLogs([]);
  };

  const handleClear = () => {
    clearLogs();
    setLocalLogs([]);
  };

  const togglePause = () => {
    setPaused(!paused);
  };

  // Filter logs by level
  const filteredLogs = levelFilter === "all"
    ? logs
    : logs.filter((log) => log.level === levelFilter);

  return (
    <div className="flex flex-col h-full p-4 md:p-6">
      <div className="flex items-center justify-between mb-4">
        <CRTHeader text="Logs en Vivo" level={2} />
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-4 mb-4">
        {/* Service selector */}
        <div className="flex items-center gap-2">
          <label className="text-xs text-[var(--bw-muted)] uppercase">Servicio:</label>
          <select
            value={selectedService}
            onChange={(e) => handleServiceChange(e.target.value)}
            className="
              bg-[var(--bw-surface)] border border-[var(--bw-border)]
              text-[var(--bw-fg)] text-sm px-3 py-1.5
              focus:border-[var(--bw-accent)] focus:outline-none
              font-mono
            "
          >
            {services.map((svc) => (
              <option key={svc} value={svc}>
                {svc}
              </option>
            ))}
          </select>
        </div>

        {/* Level filter */}
        <div className="flex items-center gap-2">
          <Filter size={14} className="text-[var(--bw-muted)]" />
          <select
            value={levelFilter}
            onChange={(e) => setLevelFilter(e.target.value as "all" | LogEntry["level"])}
            className="
              bg-[var(--bw-surface)] border border-[var(--bw-border)]
              text-[var(--bw-fg)] text-sm px-3 py-1.5
              focus:border-[var(--bw-accent)] focus:outline-none
              font-mono
            "
          >
            <option value="all">TODOS</option>
            <option value="info">INFO</option>
            <option value="warn">WARN</option>
            <option value="error">ERROR</option>
            <option value="debug">DEBUG</option>
          </select>
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-2">
          {isStreaming ? (
            <GlyphButton
              variant="danger"
              size="sm"
              onClick={stop}
              icon={<Pause size={14} />}
            >
              Detener
            </GlyphButton>
          ) : (
            <GlyphButton
              variant="primary"
              size="sm"
              onClick={startStream}
              icon={<Play size={14} />}
            >
              Iniciar
            </GlyphButton>
          )}

          <GlyphButton
            variant="ghost"
            size="sm"
            onClick={handleClear}
            icon={<Trash2 size={14} />}
          >
            Limpiar
          </GlyphButton>
        </div>

        {/* Auto-scroll toggle */}
        <label className="flex items-center gap-2 text-sm text-[var(--bw-muted)] cursor-pointer">
          <input
            type="checkbox"
            checked={autoScroll}
            onChange={(e) => setAutoScroll(e.target.checked)}
            className="accent-[var(--bw-accent)]"
          />
          Auto-scroll
        </label>

        {/* Pause button */}
        <GlyphButton
          variant={paused ? "primary" : "secondary"}
          size="sm"
          onClick={togglePause}
          icon={paused ? <Play size={14} /> : <Pause size={14} />}
        >
          {paused ? "Reanudar" : "Pausar"}
        </GlyphButton>

        {/* Status */}
        <div className="ml-auto flex items-center gap-4 text-xs text-[var(--bw-muted)]">
          {wsConnected ? (
            <span className="flex items-center gap-2">
              <Wifi size={12} className="text-[var(--bw-success)]" />
              <span className="text-[var(--bw-success)]">LIVE</span>
            </span>
          ) : (
            <span className="flex items-center gap-2">
              <WifiOff size={12} />
              <span>{wsState === "connecting" || wsState === "reconnecting" ? "CONNECTING..." : "OFFLINE"}</span>
            </span>
          )}
          {paused && (
            <span className="text-[var(--bw-warn)]">PAUSADO</span>
          )}
          <span>{filteredLogs.length} líneas</span>
        </div>
      </div>

      {/* Error display */}
      {error && (
        <CorpoCard className="mb-4 border-[var(--bw-danger)]">
          <p className="text-[var(--bw-danger)]">⚠ {error}</p>
        </CorpoCard>
      )}

      {/* Log viewer */}
      <CorpoCard className="flex-1 overflow-hidden flex flex-col">
        <div
          ref={containerRef}
          className="flex-1 overflow-y-auto font-mono text-xs leading-relaxed"
        >
          {filteredLogs.length === 0 && !isStreaming && (
            <div className="flex items-center justify-center h-full text-[var(--bw-muted)]">
              <div className="text-center">
                <Terminal size={48} className="mx-auto mb-4 opacity-50" />
                <p className="uppercase tracking-wider">ESPERANDO SEÑAL</p>
                <p className="text-xs mt-1">Selecciona un servicio e inicia el stream</p>
              </div>
            </div>
          )}

          {filteredLogs.length === 0 && isStreaming && (
            <div className="text-[var(--bw-muted)] animate-pulse">
              CONECTANDO A {selectedService.toUpperCase()}...
            </div>
          )}

          {filteredLogs.map((log, idx) => (
            <div
              key={idx}
              className={`
                py-0.5 px-2 flex items-start gap-2
                hover:bg-[var(--bw-surface-2)]
                ${log.level === "error" ? "bg-[var(--bw-danger)]/5" : ""}
                ${log.level === "warn" ? "bg-[var(--bw-warn)]/5" : ""}
              `}
            >
              <span className="text-[var(--bw-muted)] flex-shrink-0 w-28">
                {log.timestamp}
              </span>
              <HexBadge
                label={log.level.toUpperCase()}
                variant={getLevelBadgeVariant(log.level)}
                size="sm"
              />
              <span
                className={`
                  flex-1 min-w-0 break-words
                  ${log.level === "error" ? "text-[var(--bw-danger)]" : ""}
                  ${log.level === "warn" ? "text-[var(--bw-warn)]" : ""}
                  ${log.level === "debug" ? "text-[var(--bw-muted)]" : ""}
                  ${log.level === "info" ? "text-[var(--bw-fg)]" : ""}
                `}
              >
                {log.message}
              </span>
            </div>
          ))}

          <div ref={logsEndRef} />
        </div>
      </CorpoCard>
    </div>
  );
}
